import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_8f20c47c-6306-4c92-aa61-f6db9e4c740d/artifacts/cc7z0gyj_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2010_37_30%20AM.png";

export function PrivacyPage() {
  useEffect(() => { document.title = "Privacy Policy · Zumroo"; }, []);
  return (
    <LegalShell title="Privacy Policy" subtitle="Effective date: 1 February 2026 · Last updated: 26 April 2026">
      <P>This Privacy Policy explains how zumroo.com is owned and operated by <B>Sansar Sales Corporation</B> ("Zumroo", "we", "our", "us"), which collects, uses and protects your personal information when you use the Zumroo mobile application and website (collectively, the "Service").</P>
      <H>1. Information we collect</H>
      <Ul>
        <li><B>Account information:</B> mobile number (verified via OTP), display name, role (Homeowner / Broker), and account creation timestamp.</li>
        <li><B>Listing information:</B> property type, listing type (Sale / Rent), city, locality, address, price, area, BHK, description, and contact details that you choose to publish.</li>
        <li><B>Photos:</B> images you select from your device's photo library to add to a property listing.</li>
        <li><B>Payment events:</B> for brokers, the listings you unlock, the unlock fee paid (₹49 / ₹79), the payment ID and timestamp. We do <B>not</B> store card numbers, UPI IDs or banking credentials.</li>
        <li><B>Diagnostics:</B> minimal app logs (crash reports, request errors) used solely to keep the Service reliable.</li>
      </Ul>
      <H>2. Permissions we request</H>
      <P>Zumroo requests only the permissions strictly required to provide the Service:</P>
      <Ul>
        <li><B>Photos / Media:</B> required only when a homeowner taps "Add photos". Photos never leave the listing they are attached to.</li>
        <li><B>Internet:</B> required to communicate with our backend.</li>
      </Ul>
      <P>Zumroo does <B>not</B> request the Camera, Microphone, Location, Contacts or any background permission.</P>
      <H>3. How we use your information</H>
      <Ul>
        <li>Authenticate you and keep your session secure.</li>
        <li>Show your listings to brokers in Pune (and, in the future, other launched cities).</li>
        <li>Reveal your contact details only after a broker has paid the unlock fee.</li>
        <li>Operate the payment / unlock ledger for brokers.</li>
        <li>Keep the Service safe (e.g. detect and remove fraudulent listings).</li>
        <li>Communicate transactional notifications (payment receipts, listing approval status).</li>
      </Ul>
      <H>4. Sharing of information</H>
      <P>We do <B>not</B> sell or rent your personal information. We share it only:</P>
      <Ul>
        <li>With brokers who pay to unlock <i>your specific listing</i> — they receive the contact name, phone number and address you submitted with that listing.</li>
        <li>With our payment processor solely to confirm a transaction.</li>
        <li>With law enforcement when compelled by a valid Indian court order.</li>
      </Ul>
      <H>5. Data retention</H>
      <P>We keep your account and listing data for as long as your account is active. You can delete your account and all associated data from the app's Account screen, or by emailing <Email />.</P>
      <H>6. Security</H>
      <P>All data is transmitted over TLS. Passwords (where used) are stored as bcrypt hashes. Payment events are processed by a PCI-DSS-compliant processor.</P>
      <H>7. Your rights</H>
      <P>You can access, correct or delete your personal information at any time by emailing us at <Email />. We will respond within 30 days.</P>
      <H>8. Children's privacy</H>
      <P>Zumroo is not directed at users under 18 and we do not knowingly collect personal information from minors.</P>
      <H>9. Changes to this policy</H>
      <P>If we make material changes we'll notify you via the app and update the "Last updated" date above.</P>
      <H>10. Contact</H>
      <P>Questions about this policy? Email <Email />.</P>
    </LegalShell>
  );
}

export function TermsPage() {
  useEffect(() => { document.title = "Terms of Service · Zumroo"; }, []);
  return (
    <LegalShell title="Terms of Service" subtitle="Effective date: 1 February 2026">
      <P>By creating an account or using the Zumroo Service you agree to these Terms.</P>
      <H>1. Eligibility</H>
      <P>You must be 18 or older and capable of forming a binding contract under Indian law.</P>
      <H>2. Listings</H>
      <Ul>
        <li>Homeowners may post listings free of charge.</li>
        <li>You confirm that every listing is accurate, that you have the right to advertise the property, and that the contact details belong to you.</li>
        <li>Duplicate, fake, or misleading listings will be removed without notice.</li>
      </Ul>
      <H>3. Broker unlock fees</H>
      <Ul>
        <li>The unlock fee is ₹49 for Rent listings and ₹79 for Sale listings, inclusive of applicable taxes.</li>
        <li>Fees are non-refundable except where the contact details turn out to be invalid; raise the issue at <Email /> within 24 hours of unlock.</li>
      </Ul>
      <H>4. Acceptable use</H>
      <P>You agree not to scrape data, attempt to bypass the unlock paywall, or use the Service to send unsolicited communications.</P>
      <H>5. Termination</H>
      <P>We may suspend or terminate accounts that violate these Terms.</P>
      <H>6. Limitation of liability</H>
      <P>The Service is provided "as is". To the maximum extent permitted by law, Zumroo's total liability is limited to the unlock fees you paid in the 30 days preceding a claim.</P>
      <H>7. Governing law</H>
      <P>These Terms are governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts at Pune.</P>
      <H>8. Contact</H>
      <P>Questions? Reach us at <Email />.</P>
    </LegalShell>
  );
}

export function AboutPage() {
  useEffect(() => { document.title = "About · Zumroo"; }, []);
  return (
    <LegalShell title="About Zumroo" subtitle="Built in Pune. For Indian brokers and home owners.">
      <P>Zumroo is a property network that connects verified home owners directly with brokers and serious buyers across India. We started in Pune in 2026 because every broker we knew was losing money to fake portals, dead-end leads and bloated subscriptions.</P>
      <P>Our model is simple: <B>owners list for free</B>. <B>Brokers pay only when they unlock a contact</B> — ₹49 for a rent listing, ₹79 for a sale listing. No subscriptions. No commission. No surprises.</P>
      <H>What we believe</H>
      <Ul>
        <li><B>Verified, fresh inventory beats volume.</B> A 100-listing-a-day spam portal is worth less than 10 real owners ready to talk.</li>
        <li><B>Brokers are professionals, not lead-buyers.</B> The fee is small because the value is the time saved, not the contact itself.</li>
        <li><B>Privacy first.</B> Owner numbers are hidden until a broker explicitly pays — no public scraping.</li>
      </Ul>
      <H>Where we are</H>
      <P>Live in Pune. Mumbai launching soon. Expanding across Maharashtra and Tier-1/2 cities through 2026.</P>
      <H>Reach us</H>
      <P>For partnerships, press or anything else: <Email />.</P>
    </LegalShell>
  );
}

function LegalShell({ title, subtitle, children }) {
  return (
    <div className="bg-[#F7F6F2] min-h-screen">
      <Navbar logoUrl={LOGO_URL} content={{ play_store_url: "#" }} transparent={false} />
      <main className="container-z pt-20 sm:pt-24 pb-16 max-w-3xl">
        <Link to="/" className="flex items-center gap-1 text-sm text-[#5C6B62] hover:text-[#1A231E] mb-5">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="zcard p-6 sm:p-10">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A231E]">{title}</h1>
          {subtitle && <p className="text-sm text-[#5C6B62] mt-2">{subtitle}</p>}
          <div className="mt-7 prose prose-slate max-w-none">{children}</div>
        </div>
      </main>
      <Footer logoUrl={LOGO_URL} content={{ contact_phone: "+91 98765 43210", contact_email: "hello@zumroo.in" }} />
    </div>
  );
}

const P = ({ children }) => <p className="text-[#1A231E] leading-relaxed mt-3">{children}</p>;
const H = ({ children }) => <h2 className="text-xl font-bold mt-7 text-[#1A231E]">{children}</h2>;
const B = ({ children }) => <strong className="font-bold text-[#1A231E]">{children}</strong>;
const Ul = ({ children }) => <ul className="list-disc pl-6 mt-3 space-y-1.5 text-[#1A231E] leading-relaxed">{children}</ul>;
const Email = () => <a href="mailto:hello@zumroo.in" className="text-[#C86646] font-semibold hover:underline">hello@zumroo.in</a>;
